import React, { Component } from "react";
import { observer, inject } from "mobx-react";
import {
  FormGroup, InputGroup,
  Button,
  Intent,
  TextArea
} from "@blueprintjs/core";
import Toast from "helpers/toaster";
import { observable } from "mobx";
import moment from 'moment';
import DateTimeInput from 'components/DateTimeInput';
import { currency, currencySymbol } from 'helpers/currency';
import FormDrawer from 'components/FormDrawer';
import { FormSection, Choice, FieldError, MoreOptions } from 'components/FormBits';
import { PaymentSummary, formatMoney } from './TransactionModal';

const toCents = (value) => Math.round(parseFloat(value) * 100);

// Quick amounts: the whole balance, half of it, or something else.
export const amountChoiceFor = (amount, due) => {
  const due_ = parseFloat(due);
  const amount_ = parseFloat(amount);
  if (!isNaN(due_) && !isNaN(amount_) && due_ > 0) {
    if (toCents(amount_) === toCents(due_)) return 'full';
    if (toCents(amount_) === Math.round(toCents(due_) / 2)) return 'half';
  }
  return 'other';
};

export const amountFor = (choice, due) => {
  const due_ = parseFloat(due) || 0;
  if (choice === 'full') return due_.toFixed(2);
  if (choice === 'half') return (Math.round(toCents(due_) / 2) / 100).toFixed(2);
  return '';
};

export const AMOUNT_CHOICES = [
  { value: 'full', label: 'Full balance' },
  { value: 'half', label: 'Half' },
  { value: 'other', label: 'Other amount' },
];

@inject("viewStore", "invoiceStore")
@observer
class ManualTransactionModal extends Component {

  @observable transaction = {
    amount_paid: "0",
    transaction_date: moment()._d,
    payment_method: "Cash",
    notes: "",
    description: ""
  }

  @observable errors = {};
  dateTouched = false;
  amountInput = null;

  componentDidMount() {
    const {
      invoiceStore: {
        invoice: {
          object
        }
      }
    } = this.props;
    if(object && object.attributes && object.attributes) {
      this.transaction.amount_paid = object.attributes.due_amount ? object.attributes.due_amount.toString() : "0";
      this.transaction.invoice_id = object.attributes.id;
    }
  }

  handleManualTransactionDailog() {
    this.resetTransaction();
    this.props.onClose();
  }

  // Back to a blank payment (today's date, no notes) after saving or closing,
  // so the next one doesn't silently reuse the last one's notes/description.
  // The amount is filled from the invoice when the dialog opens.
  resetTransaction() {
    this.transaction = {
      amount_paid: "0",
      transaction_date: moment()._d,
      payment_method: "Cash",
      notes: "",
      description: ""
    };
  }

  onTransactionDateChange(date) {
    let {
      transaction
    } = this;
    this.dateTouched = true;
    transaction.transaction_date = date
  }

  // Same rules as before (they used to be toasts): an amount, not negative,
  // and not more than the balance.
  validate() {
    const {
      transaction,
      props: {
        invoiceStore: {
          invoice: {
            object: {
              attributes
            }
          }
        }
      }
    } = this;
    const errors = {};
    const amount = String(transaction.amount_paid === null || transaction.amount_paid === undefined ? '' : transaction.amount_paid).trim();
    if (!amount || isNaN(parseFloat(amount))) {
      errors.amount = 'Enter the amount they paid, e.g. 120.00.';
    } else if (parseFloat(amount) < 0) {
      errors.amount = 'The amount can\'t be negative.';
    } else if (parseFloat(amount) > parseFloat(attributes.due_amount)) {
      errors.amount = `That's more than the balance owed (${formatMoney(attributes.due_amount, attributes.currency_symbol)}).`;
    }
    this.errors = errors;
    return Object.keys(errors).length === 0;
  }

  processTransaction() {
    let {
      transaction,
      props: {
        invoiceStore,
        invoiceStore: {
          invoice: {
            object: {
              attributes
            }
          }
        }
      }
    } = this;

    if (invoiceStore.showManualTransactionLoader) return;
    if (!this.validate()) {
      if (this.amountInput) this.amountInput.focus();
      return;
    }

    transaction.invoice_id = attributes.id;
    invoiceStore.makeManualTransaction(transaction)
      .then(this.onSuccessfulTransaction.bind(this))
      .catch(this.onFailTransaction.bind(this))
  }

  onSuccessfulTransaction(response) {
    this.resetTransaction();
    this.props.onSuccess();
    Toast.show({
      icon: "thumbs-up",
      message: "Manual transaction added successfully!",
      intent: Intent.SUCCESS
    });
  }

  onFailTransaction(error) {
    Toast.show({
      icon: "warning-sign",
      message: "Unable to add manual transaction!",
      intent: Intent.DANGER
    });
  }

  handleOpenedEvent() {
    const {
      transaction,
      props: {
        invoiceStore: {
          invoice: {
            object
          }
        }
      }
    } = this;
    this.errors = {};
    if(object && object.attributes && object.attributes.due_amount) {
      transaction.amount_paid = object.attributes.due_amount;
    }
    // Paid today, unless someone already picked a date.
    if (!this.dateTouched) {
      transaction.transaction_date = moment()._d;
    }
    if (this.amountInput) {
      this.amountInput.focus();
      this.amountInput.select();
    }
  }

  onAmountChoice(choice, due) {
    this.transaction.amount_paid = amountFor(choice, due);
    this.errors = {};
    if (choice === 'other' && this.amountInput) {
      this.amountInput.focus();
    }
  }

  submit(event) {
    event.preventDefault();
    this.processTransaction();
  }

  render() {
    const {
      transaction,
      errors,
      props: {
        invoiceStore: {
          showManualTransactionLoader,
          invoice: {
            object
          }
        }
      }
    } = this;
    const busy = Boolean(showManualTransactionLoader);
    const close = this.handleManualTransactionDailog.bind(this);
    const attributes = object && object.attributes;
    const symbol = (attributes && attributes.currency_symbol) || currencySymbol();
    const due = attributes && attributes.due_amount;
    const amount = parseFloat(transaction.amount_paid);
    const buttonText = !isNaN(amount) && String(transaction.amount_paid).trim() !== ''
      ? `Record ${formatMoney(amount, symbol)} payment`
      : 'Record payment';
    const hasMore = Boolean(transaction.notes || transaction.description);
    return(
      <FormDrawer
        isOpen={this.props.isOpen}
        title="Record a payment"
        icon="bank-account"
        onClose={close}
        onOpened={this.handleOpenedEvent.bind(this)}
        busy={busy}
        footer={
          <>
            <Button text="Cancel" onClick={close} disabled={busy} />
            <Button
              intent={Intent.PRIMARY}
              icon="tick"
              type="submit"
              form="manual-payment-form"
              loading={busy}
            >
              {buttonText}
            </Button>
          </>
        }
      >
        <form id="manual-payment-form" onSubmit={this.submit.bind(this)} noValidate>
          <PaymentSummary attributes={attributes} />

          <FormSection
            title="How much they paid"
            hint="Cash or Interac e-Transfer they've already paid you."
            aside={<img src="/interac-eTransfer.svg" width="40" alt="Cash or Interac" />}
          >
            {due !== undefined && due !== null && parseFloat(due) > 0 && (
              <Choice
                name="Quick amount"
                value={amountChoiceFor(transaction.amount_paid, due)}
                onChange={(choice) => this.onAmountChoice(choice, due)}
                options={AMOUNT_CHOICES}
                disabled={busy}
              />
            )}
            <div className={`bp3-form-group${errors.amount ? ' has-error' : ''}`}>
              <label className="fb-label" htmlFor="manual-payment-amount">
                Amount paid ({currency()}{currencySymbol()})<span className="fb-required" aria-hidden="true"> *</span>
              </label>
              <InputGroup
                id="manual-payment-amount"
                inputRef={(input) => { this.amountInput = input; }}
                type="text"
                inputMode="decimal"
                autoComplete="off"
                placeholder="e.g. 120.00"
                aria-required="true"
                aria-invalid={errors.amount ? true : undefined}
                aria-describedby={errors.amount ? 'manual-payment-amount-error' : undefined}
                value={transaction.amount_paid}
                onChange={(event) => {
                  transaction.amount_paid = event.target.value;
                  if (this.errors.amount) this.errors = {};
                }}
              />
              <FieldError id="manual-payment-amount-error">{errors.amount}</FieldError>
            </div>
          </FormSection>

          <FormSection title="When" hint="Today unless they paid on another day.">
            <FormGroup label="Payment date">
              <DateTimeInput
                placeholder={'Payment date'}
                defaultValue={moment(transaction.transaction_date)}
                onChange={this.onTransactionDateChange.bind(this)}
              />
            </FormGroup>
          </FormSection>

          <MoreOptions label="Notes and description" count={2} open={hasMore}>
            <div className="bp3-form-group">
              <label className="fb-label" htmlFor="manual-payment-notes">Notes</label>
              <TextArea
                id="manual-payment-notes"
                fill={true}
                placeholder="e.g. Paid by the tenant at the door"
                value={transaction.notes}
                onChange={(event) => {transaction.notes = event.target.value}}
              />
            </div>

            <div className="bp3-form-group">
              <label className="fb-label" htmlFor="manual-payment-description">Description</label>
              <TextArea
                id="manual-payment-description"
                fill={true}
                placeholder="e.g. Deposit for furnace replacement"
                value={transaction.description}
                onChange={(event) => {transaction.description = event.target.value}}
              />
            </div>
          </MoreOptions>
        </form>
      </FormDrawer>
    )
  }
}

export default ManualTransactionModal;
